import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import {
  BANK_ACCOUNT_METHODS,
  type BankAccountInput,
  type BankAccountMethod,
  type EmployeeBankAccount,
} from '../bankAccounts';

const schema = z.object({
  label: z.string().max(120),
  method: z.enum(['iban', 'sepa', 'swift', 'wise', 'payoneer', 'local', 'other']),
  account_holder: z.string().max(150),
  bank_name: z.string().max(150),
  bank_address: z.string(),
  iban: z.string().max(42),
  swift_bic: z.string().max(11),
  account_number: z.string().max(64),
  routing_number: z.string().max(34),
  intermediary_bank_name: z.string().max(255),
  intermediary_swift_bic: z.string().max(11),
  intermediary_account: z.string().max(64),
  email: z.string().max(254).refine((v) => v === '' || /.+@.+\..+/.test(v), 'Enter a valid email'),
  currency: z.string().max(3),
  country: z.string().max(2),
  is_default: z.boolean(),
  notes: z.string().max(255),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  label: '', method: 'iban', account_holder: '', bank_name: '', bank_address: '', iban: '',
  swift_bic: '', account_number: '', routing_number: '',
  intermediary_bank_name: '', intermediary_swift_bic: '', intermediary_account: '',
  email: '', currency: '', country: '',
  is_default: false, notes: '',
};

const FIELD_KEYS = Object.keys(EMPTY) as (keyof FormValues)[];

function toFormValues(account: EmployeeBankAccount): FormValues {
  return {
    label: account.label, method: account.method, account_holder: account.account_holder,
    bank_name: account.bank_name, bank_address: account.bank_address,
    iban: account.iban, swift_bic: account.swift_bic,
    account_number: account.account_number, routing_number: account.routing_number,
    intermediary_bank_name: account.intermediary_bank_name,
    intermediary_swift_bic: account.intermediary_swift_bic,
    intermediary_account: account.intermediary_account,
    email: account.email, currency: account.currency, country: account.country,
    is_default: account.is_default, notes: account.notes,
  };
}

interface BankAccountFormProps {
  /** Existing account when editing; omit to create. */
  account?: EmployeeBankAccount;
  submitLabel: string;
  onSubmit: (input: BankAccountInput) => Promise<unknown>;
  onCancel: () => void;
}

export function BankAccountForm({ account, submitLabel, onSubmit, onCancel }: BankAccountFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: account ? toFormValues(account) : EMPTY,
  });

  async function handleSubmit(values: FormValues) {
    const input: BankAccountInput = { ...values, currency: values.currency.toUpperCase() };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <FormSection
          variant="card"
          title="Account"
          description="Payout destination — fill the fields relevant to the chosen method."
        >
          <div className="grid items-start gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label</FormLabel>
                  <FormControl>
                    <Input placeholder="Primary EUR" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Method</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(v) => field.onChange(v as BankAccountMethod)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {BANK_ACCOUNT_METHODS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="account_holder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account holder</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bank_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bank_address"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel className="flex items-center gap-1.5">
                    Bank address
                    <InfoHint text="Bank / branch address — often required on SWIFT / international wires." />
                  </FormLabel>
                  <FormControl>
                    <Textarea rows={2} placeholder="Bank street, city, country" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="iban"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>IBAN</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="swift_bic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SWIFT / BIC</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="account_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="routing_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Routing / sort code</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="intermediary_bank_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Intermediary bank</FormLabel>
                  <FormControl>
                    <Input placeholder="BNP PARIBAS SA, PARIS, FRANCE" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="intermediary_swift_bic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Intermediary SWIFT / BIC</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="intermediary_account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Beneficiary account at intermediary</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account email (Wise / Payoneer)</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <FormControl>
                    <Input maxLength={3} placeholder="EUR" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country (ISO)</FormLabel>
                  <FormControl>
                    <Input maxLength={2} placeholder="DE" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="is_default"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-md border p-3">
                <FormLabel className="m-0">Default payout account</FormLabel>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
